import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { LoadingScreen } from '@/components/feedback/loading-screen';
import PermissionGuard from '@/auth/guard/permission-guard';
import { OrionAccess, OrionModule, OrionSubmodule } from '@/types';

// Lazy load marketing list views
const ListListView = lazy(() =>
  import('@/sections/management/accounts/marketing/lists/views').then((module) => ({
    default: module.ListListView,
  }))
);

const ListCreateView = lazy(() =>
  import('@/sections/management/accounts/marketing/lists/views').then((module) => ({
    default: module.ListCreateView,
  }))
);

const ListEditView = lazy(() =>
  import('@/sections/management/accounts/marketing/lists/views').then((module) => ({
      default: module.ListEditView,
    })
  )
);

const ListDetailView = lazy(() =>
  import('@/sections/management/accounts/marketing/lists/views').then((module) => ({
    default: module.ListDetailView,
  }))
);

// Lazy load email campaign views
const EmailCampaignListView = lazy(() =>
  import('@/sections/management/accounts/marketing/emails/views').then((module) => ({
    default: module.EmailCampaignListView,
  }))
);

const EmailCampaignCreateView = lazy(() =>
  import('@/sections/management/accounts/marketing/emails/views').then((module) => ({
    default: module.EmailCampaignCreateView,
  }))
);

const EmailCampaignDetailView = lazy(() =>
  import('@/sections/management/accounts/marketing/emails/views').then((module) => ({
    default: module.EmailCampaignDetailView,
  }))
);

const EmailCampaignEditView = lazy(() =>
  import('@/sections/management/accounts/marketing/emails/views').then((module) => ({
    default: module.EmailCampaignEditView,
  }))
);

// Lazy load email template views
const TemplateListView = lazy(() =>
  import('@/sections/management/accounts/marketing/templates/views').then((module) => ({
    default: module.TemplateListView,
  }))
);

const TemplateCreateView = lazy(() =>
  import('@/sections/management/accounts/marketing/templates/views').then((module) => ({
    default: module.TemplateCreateView,
  }))
);

const TemplateEditView = lazy(() =>
  import('@/sections/management/accounts/marketing/templates/views').then((module) => ({
    default: module.TemplateEditView,
  }))
);

const TemplateDetailView = lazy(() =>
  import('@/sections/management/accounts/marketing/templates/views').then((module) => ({
    default: module.TemplateDetailView,
  }))
);

// Lazy load form views
const FormListPage = lazy(() => import('@/pages/management/marketing/forms/list'));
const FormCreatePage = lazy(() => import('@/pages/management/marketing/forms/create'));
const FormEditPage = lazy(() => import('@/pages/management/marketing/forms/edit'));
const FormDetailPage = lazy(() => import('@/pages/management/marketing/forms/detail'));

// Lazy load event views
const EventListPage = lazy(() => import('@/pages/management/marketing/events/list'));
const EventCreatePage = lazy(() => import('@/pages/management/marketing/events/new'));
const EventDetailPage = lazy(() => import('@/pages/management/marketing/events/view'));
const EventEditPage = lazy(() => import('@/pages/management/marketing/events/edit'));

// Lazy load leads views
const LeadsKanbanPage = lazy(() => import('@/pages/management/marketing/leads/list'));

// Lazy load sequence views
const SequenceListPage = lazy(() => import('@/pages/management/marketing/sequences/list'));
const SequenceCreatePage = lazy(() => import('@/pages/management/marketing/sequences/new'));
const SequenceDetailPage = lazy(() => import('@/pages/management/marketing/sequences/view'));
const SequenceEditPage = lazy(() => import('@/pages/management/marketing/sequences/edit'));

export const marketingRoutes = [
  {
    path: 'marketing',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: 'leads',
        element: <LeadsKanbanPage />,
      },
      {
        path: 'lists',
        children: [
          { element: <ListListView />, index: true },
          { path: 'new', element: <ListCreateView /> },
          { path: ':id', element: <ListDetailView /> },
          { path: ':id/edit', element: <ListEditView /> },
        ],
      },
      {
        path: 'emails',
        children: [
          { element: <EmailCampaignListView />, index: true },
          { path: ':id', element: <EmailCampaignDetailView /> },
          { path: ':id/edit', element: <EmailCampaignEditView /> },
          { path: 'new', element: <EmailCampaignCreateView /> },
        ],
      },
      {
        path: 'templates',
        children: [
          { element: <TemplateListView />, index: true },
          { path: 'new', element: <TemplateCreateView /> },
          { path: ':id', element: <TemplateDetailView /> },
          { path: ':id/edit', element: <TemplateEditView /> },
        ],
      },
      {
        path: 'forms',
        children: [
          { element: <FormListPage />, index: true },
          { path: 'new', element: <FormCreatePage /> },
          { path: ':id', element: <FormDetailPage /> },
          { path: ':id/edit', element: <FormEditPage /> },
        ],
      },
      {
        path: 'events',
        children: [
          { element: <EventListPage />, index: true },
          { path: 'new', element: <EventCreatePage /> },
          { path: ':id', element: <EventDetailPage /> },
          { path: ':id/edit', element: <EventEditPage /> },
        ],
      },
      {
        path: 'sequences',
        children: [
          {
            element: (
              <PermissionGuard
                module={OrionModule.ACCOUNT}
                submodule={OrionSubmodule.ACCOUNT.CONTACT}
                access={OrionAccess.VIEW}
                hasContent
              >
                <SequenceListPage />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <PermissionGuard
                module={OrionModule.ACCOUNT}
                submodule={OrionSubmodule.ACCOUNT.CONTACT}
                access={OrionAccess.CREATE}
                hasContent
              >
                <SequenceCreatePage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id',
            element: (
              <PermissionGuard
                module={OrionModule.ACCOUNT}
                submodule={OrionSubmodule.ACCOUNT.CONTACT}
                access={OrionAccess.VIEW}
                hasContent
              >
                <SequenceDetailPage />
              </PermissionGuard>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <PermissionGuard
                module={OrionModule.ACCOUNT}
                submodule={OrionSubmodule.ACCOUNT.CONTACT}
                access={OrionAccess.EDIT}
                hasContent
              >
                <SequenceEditPage />
              </PermissionGuard>
            ),
          },
        ],
      },
    ],
  },
];
